<template>
	<div class="content-page content-page--customer-error-logs">
		<ActionSetNav
			v-if="customerName"
			:name="customerName"
			:display="true"
			:actions="mainNavActions"
		/>
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">
					Error Log

					<span class="content-page__title--icon"
						><Button
							className="btn--icon-only"
							iconClassName="icon-refresh"
							:iconBeforeLabel="true"
							:btnClick="
								() => getCompanyErrorLogs(customer.companyId)
							"
							data-test="refreshButton"
					/></span>
				</h2>
			</div>
			<div class="content-page__actions">
				<Button
					className="btn--has-icon btn--small"
					iconClassName="icon-export"
					labelText="Export"
					:iconBeforeLabel="true"
				/>
			</div>
		</div>

		<ErrorLogsFilters
			v-if="hasResults"
			v-model="filters"
			:defaultFilters="defaultFilters"
			:counter="this.errorLogs.length"
		/>

		<Spinner v-if="loading" :processor="{ loading: true }" />

		<ScrollContainer v-if="hasResults">
			<Table
				class="table-container--error-logs"
				:sourceObject="tableSource"
				:isArrowUp="true"
				@change="updatedPagination"
				:tableMobilev1="true"
			>
				<template #col-group>
					<colgroup>
						<!-- Select -->
						<col class="table__col table__col--select" />

						<!-- Date/Time -->
						<col class="table__col table__col--date-time" />

						<!-- User -->
						<col class="table__col table__col--user" />

						<!-- Error # -->
						<col class="table__col table__col--error-code" />

						<!-- Location -->
						<col class="table__col table__col--location" />

						<!-- Details -->
						<col class="table__col table__col--error-details" />

						<!-- Comment -->
						<col class="table__col table__col--comment" />

						<!-- Resolved -->
						<col class="table__col table__col--resolved" />
					</colgroup>
				</template>
				<template #body-select="{ body, index }">
					<!-- DEV NOTE: cycle through ref to get all the logs ids -->
					<div class="error-logs-data--select">
						<Checkbox
							v-model="body.logId"
							:ref="`selectedRow${index}`"
						/>
					</div>
				</template>
				<template #body-timestamp="{ body }">
					<div class="error-logs-data">
						{{ fullDateString(body.timestamp) }}
						<div class="error-logs-data__text-emphasis">
							{{ differenceDateString(body.timestamp) }}
						</div>
					</div>
				</template>
				<template #body-errorDetail="{ body }">
					<div class="error-logs-data--details">
						<div class="error-logs-data__text">
							{{ detailsTextTruncated(body.errorDetail) }}
							<Button
								v-if="
									body.errorDetail.length >
										maxTextCharQuantity
								"
								labelText="read more"
								className="btn--inline-link"
								:btnClick="
									() => displayDetailsTextFn(body.errorDetail)
								"
							/>
						</div>
						<div class="error-logs-data__action">
							<Button
								className="btn--icon-only"
								iconClassName="icon-copy"
								:iconBeforeLabel="true"
								:btnClick="
									() => copyToClipboardFn(body.errorDetail)
								"
							/>
						</div>
					</div>
				</template>
				<!-- table comment column -->
				<template #body-errorMessage="{ body }">
					<div class="error-logs-data--comment">
						<div
							v-if="body.errorMessage.length"
							class="error-logs-data__text"
						>
							{{ detailsTextTruncated(body.errorMessage) }}
							<Button
								v-if="
									body.errorMessage.length >
										maxTextCharQuantity
								"
								labelText="read more"
								className="btn--inline-link"
								:btnClick="
									() =>
										displayCommentsTextFn(body.errorMessage)
								"
							/>
						</div>
						<div v-else class="error-logs-data__text">
							{{ body.errorMessage }}
						</div>
						<div class="error-logs-data__action">
							<Button
								v-if="!body.errorMessage"
								className="btn--icon-only"
								iconClassName="icon-more"
								:iconBeforeLabel="true"
								:btnClick="() => displayCommentsFn()"
							/>
							<Button
								v-else
								className="btn--icon-only"
								iconClassName="icon-edit"
								:iconBeforeLabel="true"
								:btnClick="
									() => displayCommentsFn(body.errorMessage)
								"
							/>
						</div>
					</div>
				</template>
				<template #body-isResolved="{ index }">
					<Toggle
						v-model="tableSource.source.items[index].isResolved"
					/>
				</template>
			</Table>
		</ScrollContainer>
		<Modal
			v-if="displayDetailsTextModal"
			:display="true"
			title="Error Log Details"
			@show="displayDetailsTextModal = false"
			:buttons="modalDetailsButtons"
			data-test="detailsTextModal"
		>
			<template #modal-content>
				<div class="customer-error-logs__details-modal">{{ detailsText }}</div>
			</template>
		</Modal>
		<Modal
			v-if="displayCommentsTextModal"
			:display="true"
			title="Comment"
			@show="displayCommentsTextModal = false"
			data-test="commentTextModal"
		>
			<template #modal-content>
				<div class="customer-error-logs__comment-modal">
					<Textearea
						label="Add or edit a comment to provide information for other users."
						:cols="30"
						:rows="5"
						v-model="commentsText"
						disabled
					/>
				</div>
			</template>
			<template #modal-footer>
				<Button
					labelText="Close"
					className="btn--primary"
					:btnClick="() => (displayCommentsTextModal = false)"
				/>
			</template>
		</Modal>
		<Modal
			v-if="displayCommentsModal"
			:display="true"
			title="Comment"
			@show="displayCommentsModal = false"
			data-test="commentsModal"
		>
			<template #modal-content>
				<div class="customer-error-logs__comment-modal">
					<Textearea
						label="Add or edit a comment to provide information for other users."
						:cols="30"
						:rows="5"
						v-model="commentsText"
					/>
				</div>
			</template>
			<template #modal-footer>
				<ButtonGroup>
					<template #container>
						<Button
							labelText="Save"
							className="btn--primary"
							:btnClick="() => saveComment"
						/>
						<Button
							labelText="Cancel"
							className="btn--link"
							:btnClick="() => (displayCommentsModal = false)"
						/>
					</template>
				</ButtonGroup>
			</template>
		</Modal>
		<NoResults v-if="!loading && !errorLogs.length" />
	</div>
</template>

<script>
import ActionSetNav from '@/site-components/actions-set/action-set-nav';
import ErrorLogsFilters from './error-logs-filters';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import ButtonGroup from '@/components/forms/button/button-group';
import Button from '@/components/forms/button/button';
import Toggle from '@/components/forms/selection-control/toggle/toggle';
import NoResults from '@/site-components/no-results/no-results';
import Spinner from '@/components/visual/spinner/spinner';
import Table from '@/components/shells/table/table';
import Modal from '@/components/shells/modal/modals';
import Textearea from '@/components/forms/textarea/textarea';

// mixins
import DateTimeMixin from '@/mixins/datetime-mixin';
import CustomerCardActions from '@/mixins/customers/customer-card-action';

// vuex
import { mapState, mapGetters, mapActions } from 'vuex';

// libraries
import _ from 'lodash';
import { formatISO } from 'date-fns';
import { copyToClipboard } from '@/utils/function';
import { updatePagination } from '@/utils/object';
import Checkbox from '@/components/forms/selection-control/checkbox/checkbox.vue';

export default {
	components: {
		ActionSetNav,
		ErrorLogsFilters,
		ScrollContainer,
		Breadcrumbs,
		ButtonGroup,
		Button,
		Checkbox,
		Toggle,
		NoResults,
		Spinner,
		Table,
		Modal,
		Textearea
	},
	mixins: [DateTimeMixin, CustomerCardActions],
	props: {
		// route props
		id: [Number, String],
		search: String,
		dateRange: Date,
		user: {
			type: String,
			default: 'all'
		},
		errorCode: {
			type: String,
			default: 'all'
		},
		errorStatus: {
			type: String,
			default: 'all'
		}
	},
	data() {
		return {
			loading: false,
			displayDetailsTextModal: false,
			displayCommentsTextModal: false,
			displayCommentsModal: false,
			detailsText: '',
			commentsText: '',
			filters: {
				search: this.search,
				dateRange: {
					startDate: this.formatDate(this.startDate),
					endDate: this.formatDate(this.endDate)
				},
				user: this.user,
				errorCode: this.errorCode,
				errorStatus: this.errorStatus
			},
			defaultFilters: {
				search: this.search,
				dateRange: {
					startDate: this.formatDate(this.startDate),
					endDate: this.formatDate(this.endDate)
				},
				user: this.user,
				errorCode: this.errorCode,
				errorStatus: this.errorStatus
			},
			urlNames: [
				{ name: 'Clients', data: { path: '/clients' } }
			],
			customer: null,
			mainNavActions: [],
			tableSource: {
				headers: [
					{
						key: 'select',
						label: '',
						sorteable: false
					},
					{
						key: 'timestamp',
						label: 'Date/Time',
						sorteable: false
					},
					{
						key: 'userName',
						label: 'User',
						sorteable: false
					},
					{
						key: 'errorCode',
						label: 'Error #',
						sorteable: false
					},
					{
						key: 'location',
						label: 'Location',
						sorteable: false
					},
					{
						key: 'errorDetail',
						label: 'Error Log Details',
						sorteable: false
					},
					{
						key: 'errorMessage',
						label: 'Comment',
						sorteable: false
					},
					{
						key: 'isResolved',
						label: 'Resolved',
						sorteable: false
					}
				]
			},
			maxTextCharQuantity: 90,
			modalDetailsButtons: [
				{
					className: 'btn--has-icon',
					name: 'copy',
					btnClick: () => this.copyToClipboardFn(this.detailsText),
					labelText: 'Copy',
					iconClassName: 'icon-copy',
					iconBeforeLabel: true
				},
				{
					className: 'btn--primary',
					btnClick: () => {
						this.displayDetailsTextModal = false;
					},
					labelText: 'Close',
					name: 'close'
				}
			]
		};
	},
	created() {
		// only navigate from customers.
		this.customer = this.customerById(this.id);
		if (!this.customer) {
			this.$router.push({ name: 'clients', path: '/clients' });
			return;
		}

		this.mainNavActions = this.actions;

		this.getCompanyErrorLogs(this.customer.id);
	},
	beforeRouteEnter(routeTo, routeFrom, next) {
		if (routeTo.fullPath !== routeFrom.fullPath) {
			next();
		} else {
			next(false);
		}
	},
	computed: {
		...mapState('customers', ['errorLogs']),
		...mapGetters('customers', ['customerById']),
		customerName() {
			return this.customer?.displayName || '';
		},
		hasResults() {
			return !this.loading && this.errorLogs.length;
		}
	},
	methods: {
		...mapActions('customers', ['getErrorLogs']),
		async getCompanyErrorLogs(companyId) {
			try {
				this.loading = true;

				await this.getErrorLogs(companyId);

				this.updatedPagination();

				this.loading = false;
			} catch (err) {
				this.loading = false;
			}
		},
		updatedPagination() {
			this.tableSource.source = updatePagination(
				1,
				10,
				_.cloneDeep(this.errorLogs)
			);
		},
		detailsTextTruncated(text) {
			if (text.length > this.maxTextCharQuantity) {
				return (
					text
						.split('')
						.slice(0, this.maxTextCharQuantity)
						.join('') + '...'
				);
			}
			return text;
		},
		displayDetailsTextFn(text) {
			this.detailsText = text;
			this.displayDetailsTextModal = true;
		},
		displayCommentsTextFn(text) {
			this.commentsText = text;
			this.displayCommentsTextModal = true;
		},
		displayCommentsFn(text = '') {
			this.commentsText = text;
			this.displayCommentsModal = true;
		},
		formatDate(date) {
			return date
				? formatISO(new Date(date), {
						representation: 'date'
				  })
				: undefined;
		},
		copyToClipboardFn(text) {
			copyToClipboard(text);
		},
		saveComment() {
			// DEV NOTE: save comment using vuex/api
		}
	},
	watch: {
		filters: {
			deep: true,
			handler(newValue) {
				this.$router
					.push({
						path: this.$route.path,
						query: {
							search: newValue.search || undefined,
							startDate: this.formatDate(
								newValue.dateRange.startDate
							),
							endDate: this.formatDate(
								newValue.dateRange.endDate
							),
							user: newValue.user || undefined,
							errorCode: newValue.errorCode || undefined,
							errorStatus: newValue.errorStatus || undefined
						}
					})
					.catch(() => {});
				// DEV NOTE
				console.log('Call api to update the data');
			}
		}
	}
};
</script>

<style lang="scss">
/*------------------------------------*\
  Error Log Table
\*------------------------------------*/

.table-container--error-logs {
	.table__col {
		@include mq($from: md-l) {
			&--select {
				width: 44px;
			}
			&--date-time {
				width: 140px;
			}
			&--user {
				width: 220px;
			}
			&--error-details {
				width: 300px;
			}
			&--comment {
				width: 240px;
			}
		}
	}
}

/*------------------------------------*\
  Error Log Table - Data
\*------------------------------------*/

.error-logs-data {
	&--select,
	&--details,
	&--comment {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: $gutter-lg;
	}

	&__text-emphasis {
		@include body-text('regular-emphasis');
		margin-top: $gutter-sm;
	}

	&__action {
		margin-left: auto;

		.btn--icon-only {
			font-size: 22px;
		}
	}
}

::v-deep .modal {
	@include mq($from: md) {
		width: 650px;
	}
}

::v-deep .modal__body {
	overflow: initial;
}

.customer-error-logs {
    &__details-modal {
        @include body-text('regular');
    }
}
</style>
